import type { AutomationDataChangeDto, AutomationTestDatumDto } from '@tms/shared';
import {
  ArrowRight,
  Check,
  Database,
  History,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Textarea,
  Th,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import {
  useAutomationCan,
  useDataChanges,
  useDeleteDatum,
  useImportTestData,
  useTestData,
  useUpsertDatum,
} from '../hooks';
import { useFailToast } from './shared';

const ACTION_STYLES: Record<AutomationDataChangeDto['action'], string> = {
  created: 'bg-pass-soft text-pass',
  updated: 'bg-accent-soft text-accent-fg',
  deleted: 'bg-fail-soft text-fail',
};

/**
 * The value editor: a boolean is -Dkey=true|false and Boolean.getBoolean takes
 * only "true" as true, so it is a choice, not free text that invites typos.
 */
const ValueInput = ({
  datum,
  value,
  onChange,
  label,
}: {
  datum: AutomationTestDatumDto;
  value: string;
  onChange: (value: string) => void;
  label: string;
}) =>
  datum.type === 'boolean' ? (
    <Select
      aria-label={label}
      className="h-8"
      value={value === 'true' ? 'true' : 'false'}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="false">false</option>
      <option value="true">true</option>
    </Select>
  ) : (
    <Input
      aria-label={label}
      className="h-8 font-mono"
      type={datum.type === 'number' ? 'number' : 'text'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );

const ImportDialog = ({
  projectId,
  onOpenChange,
}: {
  projectId: string;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const failToast = useFailToast();
  const bulk = useImportTestData(projectId);
  const [text, setText] = useState('');

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={t('automation.project.data.importTitle')}
      description={t('automation.project.data.importHint')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!text.trim()}
            loading={bulk.isPending}
            onClick={() =>
              bulk.mutate(text, {
                onSuccess: ({ imported }) => {
                  toast.success(t('automation.project.data.imported', { count: imported }));
                  onOpenChange(false);
                },
                onError: failToast,
              })
            }
          >
            {t('automation.project.data.importSubmit')}
          </Button>
        </>
      }
    >
      <Field label={t('automation.project.data.importLabel')}>
        {({ id }) => (
          <Textarea
            id={id}
            rows={8}
            className="font-mono"
            value={text}
            placeholder={'PHONE_4=+70000000004\nPHONE_5=+70000000005\nOTP_CODE=0000'}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
};

const HistoryDialog = ({
  projectId,
  onOpenChange,
}: {
  projectId: string;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const { relative } = useFormat();
  const changes = useDataChanges(projectId);

  return (
    <Dialog open onOpenChange={onOpenChange} title={t('automation.project.data.history')} size="lg">
      {changes.error ? (
        <ErrorState error={changes.error} onRetry={() => void changes.refetch()} />
      ) : !changes.data ? (
        <SkeletonRows rows={3} className="p-0" />
      ) : changes.data.length === 0 ? (
        <p className="text-[13px] text-ink-muted">{t('automation.project.data.historyEmpty')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {changes.data.map((change) => (
            <li key={change.id} className="flex items-start gap-3 py-2.5">
              <Badge className={ACTION_STYLES[change.action]}>
                {t(`automation.project.data.action.${change.action}`)}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[13px] font-semibold text-ink">{change.key}</p>
                <p className="break-all font-mono text-[12px] text-ink-subtle">
                  {change.from ? <span>{change.from}</span> : null}
                  {change.from && change.to ? (
                    <ArrowRight className="mx-1 inline size-3 align-middle" aria-hidden="true" />
                  ) : null}
                  {change.to ? <span className="text-ink-muted">{change.to}</span> : null}
                </p>
              </div>
              <span className="whitespace-nowrap text-[12px] text-ink-subtle">
                {t('automation.project.data.changedBy', {
                  when: relative(change.at),
                  by: change.by,
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
};

/**
 * Test data: parameters and test accounts the next runs get. QA edits them in
 * place, adds, deletes and imports in bulk; every change is in the history.
 */
export const TestDataTab = ({ projectId }: { projectId: string }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const can = useAutomationCan();
  const failToast = useFailToast();
  const data = useTestData(projectId);
  const upsert = useUpsertDatum(projectId);
  const remove = useDeleteDatum(projectId);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ key: '', value: '' });
  const [adding, setAdding] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleting, setDeleting] = useState<AutomationTestDatumDto | null>(null);

  if (data.error && !data.data) {
    return (
      <Card>
        <ErrorState error={data.error} onRetry={() => void data.refetch()} />
      </Card>
    );
  }
  if (!data.data) return <SkeletonRows rows={5} className="p-0" />;

  const rows = data.data;
  const hasCi = rows.some((row) => row.ciVar);

  const startEdit = (row: AutomationTestDatumDto) => {
    setAdding(false);
    setEditing(row.id);
    setDraft({ key: row.key, value: row.value });
  };
  const saveEdit = (id: string) =>
    upsert.mutate(
      { id, key: draft.key, value: draft.value },
      {
        onSuccess: () => {
          setEditing(null);
          toast.success(t('automation.project.data.saved'));
        },
        onError: failToast,
      },
    );
  const addRow = () =>
    upsert.mutate(
      { key: draft.key.trim(), value: draft.value },
      {
        onSuccess: () => {
          setAdding(false);
          setDraft({ key: '', value: '' });
          toast.success(t('automation.project.data.added'));
        },
        onError: failToast,
      },
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-ink">
            {t('automation.project.data.title')}
          </h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">{t('automation.project.data.hint')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setHistoryOpen(true)}>
            <History className="size-3.5" />
            {t('automation.project.data.history')}
          </Button>
          {can.run ? (
            <>
              <Button size="sm" onClick={() => setImportOpen(true)}>
                <Upload className="size-3.5" />
                {t('automation.project.data.import')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setAdding(true);
                  setDraft({ key: '', value: '' });
                }}
              >
                <Plus className="size-3.5" />
                {t('automation.project.data.add')}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Card>
        {rows.length === 0 && !adding ? (
          <EmptyState
            icon={<Database className="size-6" />}
            title={t('automation.project.data.empty')}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th className={hasCi ? 'w-[24%]' : 'w-[28%]'}>
                    {t('automation.project.data.key')}
                  </Th>
                  <Th>{t('automation.project.data.value')}</Th>
                  {hasCi ? <Th className="w-[22%]">{t('automation.project.data.ci')}</Th> : null}
                  <Th>{t('automation.project.data.changed')}</Th>
                  {can.run ? <Th className="w-1" /> : null}
                </tr>
              </thead>
              <tbody>
                {adding ? (
                  <tr>
                    <Td>
                      <Input
                        autoFocus
                        aria-label={t('automation.project.data.key')}
                        className="h-8 font-mono"
                        placeholder={t('automation.project.data.keyPlaceholder')}
                        value={draft.key}
                        onChange={(event) => setDraft({ ...draft, key: event.target.value })}
                      />
                    </Td>
                    <Td>
                      <Input
                        aria-label={t('automation.project.data.value')}
                        className="h-8 font-mono"
                        placeholder={t('automation.project.data.valuePlaceholder')}
                        value={draft.value}
                        onChange={(event) => setDraft({ ...draft, value: event.target.value })}
                      />
                    </Td>
                    {hasCi ? <Td className="text-ink-subtle">{t('common.empty')}</Td> : null}
                    <Td className="text-ink-subtle">{t('common.empty')}</Td>
                    <Td>
                      <span className="flex gap-1">
                        <Button
                          variant="primary"
                          size="sm"
                          aria-label={t('common.save')}
                          disabled={!draft.key.trim() || !draft.value}
                          loading={upsert.isPending}
                          onClick={addRow}
                        >
                          <Check className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('common.cancel')}
                          onClick={() => setAdding(false)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </span>
                    </Td>
                  </tr>
                ) : null}
                {rows.map((row) => {
                  const editingThis = editing === row.id;
                  return (
                    <tr key={row.id}>
                      <Td className="font-mono font-semibold">
                        {editingThis ? (
                          <Input
                            aria-label={t('automation.project.data.key')}
                            className="h-8 font-mono"
                            value={draft.key}
                            onChange={(event) => setDraft({ ...draft, key: event.target.value })}
                          />
                        ) : (
                          row.key
                        )}
                      </Td>
                      <Td className="break-all font-mono">
                        {editingThis ? (
                          <ValueInput
                            datum={row}
                            label={t('automation.project.data.value')}
                            value={draft.value}
                            onChange={(value) => setDraft({ ...draft, value })}
                          />
                        ) : (
                          row.value || <span className="text-ink-subtle">{t('common.empty')}</span>
                        )}
                      </Td>
                      {hasCi ? (
                        <Td>
                          {row.ciVar ? (
                            <span className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1">
                                {row.wiredInCi ? (
                                  <Zap
                                    className="size-3 text-pass"
                                    aria-label={t('automation.project.data.wiredInCi')}
                                  />
                                ) : null}
                                <span className="font-mono text-[12px]">{row.ciVar}</span>
                              </span>
                              {row.source ? (
                                <span className="font-mono text-[11px] text-ink-subtle">
                                  {row.source}
                                </span>
                              ) : null}
                            </span>
                          ) : (
                            <span className="text-ink-subtle">{t('common.empty')}</span>
                          )}
                        </Td>
                      ) : null}
                      <Td className="whitespace-nowrap text-[12px] text-ink-subtle">
                        {t('automation.project.data.changedBy', {
                          when: relative(row.updatedAt),
                          by: row.updatedBy,
                        })}
                      </Td>
                      {can.run ? (
                        <Td>
                          <span className="flex gap-1">
                            {editingThis ? (
                              <>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  aria-label={t('common.save')}
                                  loading={upsert.isPending}
                                  onClick={() => saveEdit(row.id)}
                                >
                                  <Check className="size-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={t('common.cancel')}
                                  onClick={() => setEditing(null)}
                                >
                                  <X className="size-3.5" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={t('automation.project.data.edit', { key: row.key })}
                                  onClick={() => startEdit(row)}
                                >
                                  <Pencil className="size-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-fail"
                                  aria-label={t('automation.project.data.delete', { key: row.key })}
                                  onClick={() => setDeleting(row)}
                                >
                                  <Trash2 className="size-3.5" />
                                </Button>
                              </>
                            )}
                          </span>
                        </Td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {importOpen ? <ImportDialog projectId={projectId} onOpenChange={setImportOpen} /> : null}
      {historyOpen ? <HistoryDialog projectId={projectId} onOpenChange={setHistoryOpen} /> : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={t('automation.project.data.deleteTitle')}
        description={t('automation.project.data.deleteDescription', { key: deleting?.key ?? '' })}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => {
          if (!deleting) return;
          const key = deleting.key;
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success(t('automation.project.data.deleted', { key }));
              setDeleting(null);
            },
            onError: failToast,
          });
        }}
      />
    </div>
  );
};
